import React, { useEffect, useRef, useState } from 'react';
import { SpinePlayer } from '@esotericsoftware/spine-player';
import '@esotericsoftware/spine-player/dist/spine-player.css';

const CURSOR_FOLLOW_RANGE = {
  minX: -10,
  maxX: 300,
  minY: -1200,
  maxY: 300,
};

const ANIMATION_MIX_SECONDS = 0.15;

const MOBILE_BREAKPOINT_PX = 768;

function isViewportMobile() {
  if (typeof window === 'undefined') return false; // SSR guard
  if (MOBILE_BREAKPOINT_PX <= 0) return false;
  return window.innerWidth <= MOBILE_BREAKPOINT_PX;
}

const VIEWPORT = {
  x: -1800,
  y: -1600,
  width: 3400,
  height: 4200,
};

const CHEST_INTERRUPT_LINES = [
  { line: "A little too fast don't ya think?", voiceUrl: null },
  { line: "Slow down a bit will ya?", voiceUrl: null },
  { line: "Excited much?", voiceUrl: null },
];

const CROTCH_INTERRUPT_LINES = [
  { line: "Mmm... Yeah...", voiceUrl: null },
  { line: "Ah... right there...", voiceUrl: null },
  { line: "Don't stop... that feels good...", voiceUrl: null },
  { line: "Oh yes... keep going...", voiceUrl: null },
  { line: "Yes... just like that...", voiceUrl: null },
];

const REACTIONS = {
  chest_annoyed: {
    animation: 'breast_flick',
    returnToAnimation: 'idle',
    expression: 'look_away_flat',
    expressionHoldSeconds: 2.0,
    line: "My eyes are up here y'know...",
    voiceUrl: null, // e.g. '/audio/chest-annoyed.mp3'
    interruptLines: CHEST_INTERRUPT_LINES,
  },
  chest_annoyed_2: {
    animation: 'breast_flick',
    returnToAnimation: 'idle',
    expression: 'look_away_flat',
    expressionHoldSeconds: 2.0,
    line: "Careful where you touch...",
    voiceUrl: null,
    interruptLines: CHEST_INTERRUPT_LINES,
  },
  chest_annoyed_3: {
    animation: 'breast_flick',
    returnToAnimation: 'idle',
    expression: 'look_away_flat',
    expressionHoldSeconds: 2.0,
    line: "Wow, going right for them huh?",
    voiceUrl: null,
    interruptLines: CHEST_INTERRUPT_LINES,
  },

  crotch_surprised: {
    animation: 'crotch_defense',
    returnToAnimation: 'idle',
    expression: 'surprised',
    expressionHoldSeconds: 4.0,
    line: "Woah, watch it!",
    voiceUrl: null,
    //interruptLines: CROTCH_INTERRUPT_LINES,
  },
  crotch_uneasy: {
    animation: 'crotch_defense',
    returnToAnimation: 'idle',
    expression: 'surprised',
    expressionHoldSeconds: 4.0,
    line: "I said watch where you're clicking!",
    voiceUrl: null,
    //interruptLines: CROTCH_INTERRUPT_LINES,
  },
  crotch_resisting: {
    animation: 'crotch_defense',
    returnToAnimation: 'idle',
    expression: 'look_away_flat',
    expressionHoldSeconds: 4.0,
    line: "Quit... Mmmm... touching me there... ah~...",
    voiceUrl: null,
    //interruptLines: CROTCH_INTERRUPT_LINES,
  },
  crotch_give_up: {
    animation: 'crotch_defense',
    returnToAnimation: 'idle',
    expression: 'look_away_flat',
    expressionHoldSeconds: 4.0,
    line: "Hahh~.... you're not gonna stop... are you?",
    voiceUrl: null,
    //interruptLines: CROTCH_INTERRUPT_LINES,
  },
  crotch_amused: {
    animation: 'leg_lift',
    returnToAnimation: 'idle',
    expression: 'half_squint',
    expressionHoldSeconds: 4.0,
    line: "Fine.... if you're gonna do it... then do it right...",
    voiceUrl: null,
    interruptBehavior: 'resume',
    resumeAtPercent: 0.2,
    interruptLines: CROTCH_INTERRUPT_LINES,
  },
};

const INTERACTION_HOTSPOTS = [
  {
    label: 'chest',
    rect: { top: '45%', left: '40%', width: '40%', height: '12%' },
    selectionMode: 'random',
    reactions: ['chest_annoyed', 'chest_annoyed_2', 'chest_annoyed_3'],
  },
  {
    label: 'lower',
    rect: { top: '70%', left: '42%', width: '25%', height: '15%' },
    selectionMode: 'sequence',
    reactions: ['crotch_surprised', 'crotch_uneasy', 'crotch_resisting', 'crotch_give_up', 'crotch_amused'],
  },
];

const SPEAK_EVENT_NAME = 'companion-speak';

function pickReaction(hotspot, hotspotState) {
  const pool = (hotspot.reactions || [])
    .map((key) => ({ key, reaction: REACTIONS[key] }))
    .filter((entry) => {
      if (!entry.reaction) {
        console.warn(`Hotspot "${hotspot.label}" references unknown reaction key "${entry.key}" — check REACTIONS.`);
        return false;
      }
      return true;
    });

  if (pool.length === 0) return null;

  if (hotspot.selectionMode === 'sequence') {
    const index = Math.min(hotspotState.clickCount, pool.length - 1);
    return pool[index];
  }

  // Default: 'random'. Avoid repeating the same reaction twice in a row
  // when there's more than one option, so it doesn't feel stuck.
  let choices = pool;
  if (pool.length > 1 && hotspotState.lastReactionKey) {
    const filtered = pool.filter((entry) => entry.key !== hotspotState.lastReactionKey);
    if (filtered.length > 0) choices = filtered;
  }
  return choices[Math.floor(Math.random() * choices.length)];
}

const SpineCompanion = () => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(isViewportMobile);
  const hotspotStateRef = useRef({});

  useEffect(() => {
    const handleResize = () => setIsMobile(isViewportMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      return;
    }

    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const percentX = mouseX / rect.width;
      const percentY = mouseY / rect.height;

      const clampedPercentX = Math.max(0, Math.min(1, percentX));
      const clampedPercentY = Math.max(0, Math.min(1, percentY));

      const { minX, maxX, minY, maxY } = CURSOR_FOLLOW_RANGE;

      mousePos.current.x = minX + clampedPercentX * (maxX - minX);
      mousePos.current.y = maxY - clampedPercentY * (maxY - minY);
    };

    window.addEventListener('mousemove', handleMouseMove);

    if (!playerRef.current) {
      playerRef.current = new SpinePlayer(containerRef.current, {
        skeleton: '/spine/Erika.json',
        atlas: '/spine/Erika.atlas',
        animation: 'idle',
        alpha: true,
        backgroundColor: '#00000000',
        showControls: false,
        showLoading: false,
        defaultMix: ANIMATION_MIX_SECONDS,
        viewport: {
          x: VIEWPORT.x,
          y: VIEWPORT.y,
          width: VIEWPORT.width,
          height: VIEWPORT.height,
          padLeft: '0%',
          padRight: '0%',
          padTop: '0%',
          padBottom: '0%',
        },

        update: (player, delta) => {
          const skeleton = player.skeleton;
          if (!skeleton) return;

          const targetBone = skeleton.findBone('bone_cursor_target');
          if (!targetBone) {
            console.warn('⚠️ Bone "bone_cursor_target" not found on skeleton.');
            return;
          }

          const setupX = targetBone.data.setupPose.x;
          const setupY = targetBone.data.setupPose.y;

          targetBone.pose.x = setupX + mousePos.current.x;
          targetBone.pose.y = setupY + mousePos.current.y;

          skeleton.updateWorldTransform();
        },

        success: () => {
          setIsReady(true);
        },

        error: (player, msg) => {
          console.error('Spine player error:', msg);
          setIsReady(true);
        },
      });
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [isMobile]);

  const handleHotspotClick = (hotspot) => {
    if (!playerRef.current || !playerRef.current.animationState) return;

    if (!hotspotStateRef.current[hotspot.label]) {
      hotspotStateRef.current[hotspot.label] = { clickCount: 0, lastReactionKey: null };
    }
    const hotspotState = hotspotStateRef.current[hotspot.label];

    const picked = pickReaction(hotspot, hotspotState);
    if (!picked) return;
    const reaction = picked.reaction;

    hotspotState.clickCount += 1;
    hotspotState.lastReactionKey = picked.key;

    const animState = playerRef.current.animationState;

    const previousEntry = animState.getTrack(0);
    const wasAlreadyPlayingThisAnimation =
      !!previousEntry &&
      !!previousEntry.animation &&
      previousEntry.animation.name === reaction.animation;

    const newEntry = animState.setAnimation(0, reaction.animation, false);

    if (
      reaction.interruptBehavior === 'resume' &&
      wasAlreadyPlayingThisAnimation &&
      newEntry
    ) {
      let resumeTime = reaction.resumeAtSeconds;
      if (resumeTime == null && reaction.resumeAtPercent != null && newEntry.animation) {
        resumeTime = newEntry.animation.duration * reaction.resumeAtPercent;
      }
      if (resumeTime != null) {
        newEntry.trackTime = resumeTime;
      }
    }

    animState.addAnimation(0, reaction.returnToAnimation || 'idle', true, 0);

    if (reaction.expression) {
      animState.setAnimation(1, reaction.expression, false);
      animState.addEmptyAnimation(1, 0.5, reaction.expressionHoldSeconds ?? 2.0);
    }

    let spokenLine = reaction.line;
    let spokenVoiceUrl = reaction.voiceUrl || null;
    if (wasAlreadyPlayingThisAnimation && reaction.interruptLines && reaction.interruptLines.length > 0) {
      const altLine = reaction.interruptLines[Math.floor(Math.random() * reaction.interruptLines.length)];
      spokenLine = altLine.line;
      spokenVoiceUrl = altLine.voiceUrl || null;
    }

    if (spokenLine) {
      window.dispatchEvent(
        new CustomEvent(SPEAK_EVENT_NAME, {
          detail: { text: spokenLine, audioSrc: spokenVoiceUrl },
        })
      );
    }
  };

  if (isMobile) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '0px',
        right: '0px',
        width: '450px',
        aspectRatio: '2/3',
        zIndex: 9999,
        opacity: isReady ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {INTERACTION_HOTSPOTS.map((hotspot) => (
        <div
          key={hotspot.label}
          onClick={() => handleHotspotClick(hotspot)}
          style={{ position: 'absolute', cursor: 'pointer', ...hotspot.rect }}
        />
      ))}
    </div>
  );
};

export default SpineCompanion;
